import { Temporal } from "temporal-polyfill";
import { MetricsPeriodType } from "@rmp/core/types/metrics/metricsPeriodType";
import { i18n } from "@rmp/core/plugins";
import { MetricsPeriod } from "@rmp/core/types/metrics/metricsPeriod";
import { getCurrentCalendarDate, toCalendarDate } from "@rmp/core/types/calendarDate";
import { Modify } from "@rmp/core/types/Modify";
import {
	ApiDivisionBranchBasinPersisted,
	ApiDivisionDepartmentPersisted,
	ApiDivisionHeadPersisted,
	ApiDivisionKind, ApiDivisionPersistedBase, ApiDivisionPortPersisted,
	ApiServiceType
} from "@rmp/api-gateway";
import { groupBy } from "lodash";
import { SERVICE_PRESENTATIONS } from "@rmp/core/types/services/workflow/serviceProfile";

export const getServiceIcon = (serviceType: ApiServiceType): string => {
	return SERVICE_PRESENTATIONS[serviceType]?.ui.icon ?? "";
};


interface GetMetricsPeriodsParams {
	noCustomPeriod?: boolean;
}

export const getMetricsPeriods = (params?: GetMetricsPeriodsParams): MetricsPeriod[] => {
	const currentDate = Temporal.PlainDate.from(getCurrentCalendarDate());
	const currentMonthStart = currentDate.with({ day: 1 });
	const pastMonthStart = currentMonthStart.subtract({ months: 1 });
	const currentQuarterStart = currentDate.with({
		month: Math.floor((currentDate.month - 1) / 3) * 3 + 1,
		day: 1
	});
	const pastQuarterStart = currentQuarterStart.subtract({ months: 3 });
	const currentYearStart = currentDate.with({ month: 1, day: 1 });
	const pastYearStart = currentYearStart.subtract({ years: 1 });
	const asCalendarDate = (value: Temporal.PlainDate) => toCalendarDate(value.toString());
	
	const items: MetricsPeriod[] = [
		{
			text: i18n.tc(`metrics.periods.${MetricsPeriodType.CURRENT_MONTH}`),
			value: MetricsPeriodType.CURRENT_MONTH,
			start: asCalendarDate(currentMonthStart),
			end: asCalendarDate(currentDate)
		},
		{
			text: i18n.tc(`metrics.periods.${MetricsPeriodType.PAST_MONTH}`),
			value: MetricsPeriodType.PAST_MONTH,
			start: asCalendarDate(pastMonthStart),
			end: asCalendarDate(currentMonthStart.subtract({ days: 1 }))
		},
		{
			text: i18n.tc(`metrics.periods.${MetricsPeriodType.CURRENT_QUARTER}`),
			value: MetricsPeriodType.CURRENT_QUARTER,
			start: asCalendarDate(currentQuarterStart),
			end: asCalendarDate(currentDate)
		},
		{
			text: i18n.tc(`metrics.periods.${MetricsPeriodType.PAST_QUARTER}`),
			value: MetricsPeriodType.PAST_QUARTER,
			start: asCalendarDate(pastQuarterStart),
			end: asCalendarDate(currentQuarterStart.subtract({ days: 1 }))
		},
		{
			text: i18n.tc(`metrics.periods.${MetricsPeriodType.CURRENT_YEAR}`),
			value: MetricsPeriodType.CURRENT_YEAR,
			start: asCalendarDate(currentYearStart),
			end: asCalendarDate(currentDate)
		},
		{
			text: i18n.tc(`metrics.periods.${MetricsPeriodType.PAST_YEAR}`),
			value: MetricsPeriodType.PAST_YEAR,
			start: asCalendarDate(pastYearStart),
			end: asCalendarDate(currentYearStart.subtract({ days: 1 }))
		},
		{
			text: i18n.tc(`metrics.periods.${MetricsPeriodType.ALL_TIME}`),
			value: MetricsPeriodType.ALL_TIME,
			start: toCalendarDate("2024-01-01"),
			end: asCalendarDate(currentDate)
		}
	];
	
	if(!params?.noCustomPeriod)
		items.push({
			text: i18n.tc(`metrics.periods.${MetricsPeriodType.PERIOD}`),
			value: MetricsPeriodType.PERIOD,
			start: "",
			end: ""
		});
	
	return items;
};

// Helper для построения дерева подразделений

export type NormalizedDivision = {
	name: string;
	fullName: string;
	code: string;
	type: ApiDivisionKind;
	isArchived: boolean;
	canActAsServiceProvider: boolean;
	// branch, department
	actualAddress?: string;
	// department
	kpp?: string | null;
	// head
	inn?: string;
	ogrn?: string;
	opf?: string;
	legalAddress?: string;
	registrationDate?: string;
	// port
	latitude?: number;
	longitude?: number;
};

export type NormalizedDivisionPersisted = Modify<ApiDivisionPersistedBase, {
	division: NormalizedDivision;
}>;

export interface DivisionsTreeNode {
	id: string;
	type: "branch" | "department" | "port" | "head";
	data: NormalizedDivisionPersisted;
	children: DivisionsTreeNode[];
}

const compareDivisionNodes = (left: DivisionsTreeNode, right: DivisionsTreeNode): number => {
	const leftDivision = left.data.division;
	const rightDivision = right.data.division;
	return leftDivision.name.localeCompare(rightDivision.name, "ru")
		|| leftDivision.code.localeCompare(rightDivision.code, "ru")
		|| left.id.localeCompare(right.id);
};

const sortDivisionTree = (nodes: DivisionsTreeNode[]): DivisionsTreeNode[] =>
	nodes
	.map(node => ({ ...node, children: sortDivisionTree(node.children) }))
	.sort(compareDivisionNodes);

function normalizeBranch(p: ApiDivisionBranchBasinPersisted): NormalizedDivisionPersisted {
	return {
		...p,
		division: {
			...p.division,
			name: p.division.shortName
		}
	};
}

function normalizeDepartment(p: ApiDivisionDepartmentPersisted): NormalizedDivisionPersisted {
	return {
		...p,
		division: {
			...p.division,
			name: p.division.shortName
		}
	};
}

function normalizePort(p: ApiDivisionPortPersisted): NormalizedDivisionPersisted {
	return {
		...p,
		division: {
			...p.division,
			name: p.division.fullName
		}
	};
}

function normalizeHead(p: ApiDivisionHeadPersisted): NormalizedDivisionPersisted {
	return {
		...p,
		division: {
			...p.division,
			name: p.division.shortName
		}
	};
}

export function buildDivisionsTree({
	branches,
	departments,
	ports,
	head
}: {
	branches: ApiDivisionBranchBasinPersisted[];
	departments: ApiDivisionDepartmentPersisted[];
	ports?: ApiDivisionPortPersisted[];
	head?: ApiDivisionHeadPersisted;
}): DivisionsTreeNode[] {
	const portsByParent = groupBy(ports ?? [], p => p.division.parentId);
	const departmentsByParent = groupBy(departments, d => d.division.parentId);
	
	const makePortNode = (p: ApiDivisionPortPersisted): DivisionsTreeNode => ({
		id: p.id,
		type: "port",
		data: normalizePort(p),
		children: []
	});
	
	const headNode: DivisionsTreeNode | null = head
		? { id: head.id, type: "head", data: normalizeHead(head), children: [] }
		: null;
	
	const branchNodes: DivisionsTreeNode[] = branches.map(branch => ({
		id: branch.id,
		type: "branch",
		data: normalizeBranch(branch),
		children: [
			...(portsByParent[branch.id] ?? []).map(makePortNode),
			...(departmentsByParent[branch.id] ?? []).map((d): DivisionsTreeNode => ({
				id: d.id,
				type: "department",
				data: normalizeDepartment(d),
				children: (portsByParent[d.id] ?? []).map(makePortNode)
			}))
		]
	}));
	
	return sortDivisionTree([...(headNode ? [headNode] : []), ...branchNodes]);
}

